import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import api from "@/lib/api";
import { AuthShell } from "@/pages/Login";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "Check your inbox for a reset link.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset link. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      side={
        <>
          <p className="font-editorial italic text-[color:var(--ink-muted)]">Access · Recovery</p>
          <h1 className="mt-6 font-editorial text-5xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-6xl">
            Reset your password.
          </h1>
          <p className="mt-6 max-w-md text-[16px] leading-relaxed text-[color:var(--ink-body)]">
            Enter the email tied to your desk. We'll send a secure, single-use link that expires in 30 minutes.
          </p>
        </>
      }
    >
      {sent ? (
        <div className="grid gap-5 text-center" data-testid="forgot-sent">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]">
            <MailCheck size={26} />
          </div>
          <h2 className="font-editorial text-2xl text-[color:var(--ink)]">Check your inbox</h2>
          <p className="text-[15px] text-[color:var(--ink-body)]">
            If an account exists for <strong>{email}</strong>, a password reset link is on its way. The link expires in 30 minutes.
          </p>
          <Link to="/login" className="btn-edge mt-2 w-full justify-center" data-testid="forgot-back-login">
            Back to sign in <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="grid gap-5" data-testid="forgot-form">
          <div>
            <label className="mb-2 block font-editorial italic text-[14px] text-[color:var(--ink-muted)]">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@edge.trade"
              data-testid="forgot-email"
              className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
            />
          </div>
          <button type="submit" disabled={submitting} data-testid="forgot-submit" className="btn-edge mt-2 w-full justify-center disabled:cursor-wait disabled:opacity-70">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Send reset link <ArrowRight size={16} /></>}
          </button>
          <p className="mt-2 text-center text-[14px] text-[color:var(--ink-muted)]">
            Remembered it?{" "}
            <Link to="/login" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="forgot-link-login">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
